import { makeTokenProvider, type TokenProvider } from '@helios/powersync-auth-bridge-core';

/**
 * Function signature for Firebase's getIdToken.
 * @param forceRefresh - If true, forces token refresh even if not expired
 */
export type GetIdTokenFn = (forceRefresh?: boolean) => Promise<string | null>;

/**
 * Creates a PowerSync-compatible token provider using Firebase ID tokens.
 *
 * This provider wraps the Firebase `getIdToken` function to supply
 * authentication credentials to PowerSync. In Phase 1, PowerSync
 * authenticates directly using the Firebase ID token.
 *
 * @param getIdToken - Firebase's getIdToken function from auth-firebase-web
 * @param endpoint - PowerSync endpoint URL (required)
 * @returns TokenProvider function for PowerSync client configuration
 *
 * @example
 * ```ts
 * import { getIdToken } from '@helios/auth-firebase-web';
 * import { makeFirebaseTokenProvider } from '@helios/powersync-auth-bridge-web';
 *
 * const tokenProvider = makeFirebaseTokenProvider(
 *   getIdToken,
 *   'https://powersync.example.com'
 * );
 *
 * const powerSyncClient = new PowerSyncDatabase({
 *   // ...
 *   tokenProvider,
 * });
 * ```
 */
export function makeFirebaseTokenProvider(
  getIdToken: GetIdTokenFn,
  endpoint: string,
): TokenProvider {
  return makeTokenProvider(async () => {
    const token = await getIdToken(false);
    if (!token) {
      throw new Error('Failed to get Firebase ID token');
    }
    return token;
  }, endpoint);
}
